import { Quaternion } from './Quaternion';
import { TransformPosition, TransformRotation } from '@ikea-aoa/elements-3d';

export class TransformVector {
	constructor(public x?: number, public y?: number, public z?: number) {}

	public static rotate(target: TransformPosition, rotation: TransformRotation): TransformPosition {
		const quaternion = Quaternion.fromVector(rotation as { x: number; y: number; z: number });
		const result = this.rotateByQuaternion(target, quaternion);
		return result;
	}

	public static distanceSquared(a: TransformPosition, b: TransformPosition): number {
		return (
			Math.pow((b.x as number) - (a.x as number), 2) +
			Math.pow((b.y as number) - (a.y as number), 2) +
			Math.pow((b.z as number) - (a.z as number), 2)
		);
	}

	public static distance(a: TransformPosition, b: TransformPosition): number {
		return Math.sqrt(TransformVector.distanceSquared(a, b));
	}

	public static sub(a: TransformPosition, b: TransformPosition): TransformPosition {
		return {
			x: (a.x as number) - (b.x as number),
			y: (a.y as number) - (b.y as number),
			z: (a.z as number) - (b.z as number),
		};
	}

	public static add(a: TransformPosition, b: TransformPosition): TransformPosition {
		return {
			x: (a.x as number) + (b.x as number),
			y: (a.y as number) + (b.y as number),
			z: (a.z as number) + (b.z as number),
		};
	}

	public static clone(position: TransformPosition): TransformPosition {
		return {
			x: position.x,
			y: position.y,
			z: position.z,
		};
	}

	public static zero(): TransformPosition {
		return {
			x: 0,
			y: 0,
			z: 0,
		};
	}

	public static toFixed(position: TransformPosition, fractionDigits: number): TransformPosition {
		Object.entries(position).forEach(
			([key, value]) => (position[key] = parseFloat(value.toFixed(fractionDigits)))
		);
		return position;
	}

	public static isEqual(a: TransformPosition, b: TransformPosition): boolean {
		let isEqual = true;
		Object.keys(a).forEach((key) => {
			if (a[key] !== b[key]) {
				isEqual = false;
			}
		});
		return isEqual;
	}

	public static isEqualWithEpsilon(
		a: TransformPosition,
		b: TransformPosition,
		epsilon: number
	): boolean {
		let isEqual = true;
		Object.keys(a).forEach((key) => {
			if (Math.abs(a[key] - b[key]) >= epsilon) {
				isEqual = false;
			}
		});
		return isEqual;
	}

	public static rotateByQuaternion(
		vector: TransformPosition,
		quaternion: Quaternion
	): TransformPosition {
		const w1 = quaternion.w;
		const x1 = quaternion.x;
		const y1 = quaternion.y;
		const z1 = quaternion.z;

		const x2 = vector.x;
		const y2 = vector.y;
		const z2 = vector.z;

		const w3 = -x1 * (x2 as number) - y1 * (y2 as number) - z1 * (z2 as number);
		const x3 = w1 * (x2 as number) + y1 * (z2 as number) - z1 * (y2 as number);
		const y3 = w1 * (y2 as number) + z1 * (x2 as number) - x1 * (z2 as number);
		const z3 = w1 * (z2 as number) + x1 * (y2 as number) - y1 * (x2 as number);

		const x4 = x3 * w1 - w3 * x1 - y3 * z1 + z3 * y1;
		const y4 = y3 * w1 - w3 * y1 - z3 * x1 + x3 * z1;
		const z4 = z3 * w1 - w3 * z1 - x3 * y1 + y3 * x1;

		return { x: x4, y: y4, z: z4 };
	}
}
